import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Building2,
  Check,
  CheckCircle2,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  Plus,
  Star,
  X as CloseIcon,
    Wallet,
} from "lucide-react";
import { useLocation, useParams } from "react-router-dom";
import providersService from "../../../api/services/providersService";
import { validateUploadFile } from "../../../utils/directUpload";
import { getApiErrorMessage } from "../../../utils/apiError";
import axiosInstance from "../../../api/Axiosinstance";
import { ENDPOINTS } from "../../../api/Endpoints";
import AppSelect from "../../../components/AppSelect";
import {
  deriveAssistantNotIncluded,
  normalizeAssistantServices,
} from "../../../data/assistantServiceOptions";
import { resolveApprovalState } from "./approvalState";
import { useProfileStore } from "../../../hooks/useProfileStore";
import { useAuthStore } from "../../../hooks/useAuthStore";
import { useAvailabilityQuery, useUpdateAvailabilityMutation } from "../../../api/query/useProfileQueries";

const defaultAssistant = {
  id: "abdulsalam",
  name: "Abdulsalam Jaji",
  date: "2018",
  rating: 0,
  reviewsCount: 0,
  location: "Abuja, Nigeria",
  languages: ["English", "Yoruba", "Hausa"],
  price: "₦300,000.00",
  durationLabel: "4 Day trip",
  discount: "10% discount compared to daily",
  image: "/images/travel-img.png",
  contactEmail: "abdulsalam@pulo.com",
  contactPhone: "+234 800 000 0000",
  about: "",
  expectations:
    "A personalized experience tailored to your preferences. Guided tours of key attractions, culture-rich storytelling, restaurant recommendations, and transport coordination.",
  servicesOffered: [
    "Experience",
    "Miscellaneous Fee",
    "Drop Off & Pick Up",
    "Escort",
    "Security Personal",
    "Gate Pass",
  ],
  notIncluded: [],
  highlights: [],
  reviews: [],
  bookingsCount: 0,
  withdrawalAccounts: [],
  faqs: [
    {
      question: "What is your cancellation policy?",
      answer:
        "Pulo's policy applies: travellers can cancel up to 12 hours before the trip starts and get everything back except Pulo's 20% commission. After that the booking can no longer be cancelled.",
    },
    {
      question: "What is the group size for tours?",
      answer: "Private tours up to 6 people. Larger groups can be arranged on request.",
    },
    {
      question: "Are there any age restrictions for tours?",
      answer: "Family-friendly tours with no strict age limit. Some activities may have age guidance.",
    },
  ],
};

const fallbackBanks = [
  { name: "Access Bank", code: "044", currency: "NGN" },
  { name: "GTBank", code: "058", currency: "NGN" },
  { name: "Zenith Bank", code: "057", currency: "NGN" },
  { name: "UBA Bank", code: "033", currency: "NGN" },
  { name: "First Bank", code: "011", currency: "NGN" },
];

const normalizeLanguages = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((lang) => lang.trim())
      .filter(Boolean);
  }
  if (value && typeof value === "object") {
    return Object.values(value)
      .flat()
      .map((lang) => (typeof lang === "string" ? lang.trim() : ""))
      .filter(Boolean);
  }
  return [];
};

const normalizeImageList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (!item) return null;
        if (typeof item === "string") return item;
        return (
          item.url ||
          item.imageUrl ||
          item.image?.url ||
          item.secure_url ||
          item.path ||
          item.fileUrl ||
          item.filename ||
          null
        );
      })
      .filter(Boolean);
  }
  if (typeof value === "object") {
    return normalizeImageList(
      value.images || value.vehicleImages || value.vehicle_images || value.photos
    );
  }
  return [];
};

const formatReviewDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const normalizeReviewList = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((review, index) => {
      const author = review?.user || review?.reviewer || review?.traveler || {};
      const name =
        review?.name ||
        author?.name ||
        author?.fullName ||
        [author?.firstName, author?.lastName].filter(Boolean).join(" ").trim() ||
        "Anonymous";
      const comment =
        review?.comment ||
        review?.review ||
        review?.message ||
        review?.content ||
        "";
      const avatar =
        review?.avatar ||
        author?.avatar ||
        author?.profilePicture?.url ||
        author?.image ||
        "";
      const rawRating =
        review?.rating ??
        review?.stars ??
        review?.score ??
        review?.value ??
        null;
      const rating = Number(rawRating);

      return {
        id: review?.id || review?._id || `review-${index}`,
        name,
        comment,
        avatar,
        date: formatReviewDate(review?.date || review?.createdAt || review?.created_at),
        rating: Number.isFinite(rating) ? rating : null,
      };
    })
    .filter((review) => review.comment || review.rating || review.name);
};

const formatCurrency = (value, currency = "₦") => {
  const amount = Number(value || 0);
  if (Number.isNaN(amount)) return `${currency}0.00`;
  return `${currency}${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const parseCurrencyAmount = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  if (!cleaned) return 0;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sanitizeCurrencyInput = (value) => {
  const raw = String(value || "");
  const digitsOnly = raw.replace(/[^0-9.]/g, "");
  const [whole = "", ...decimalParts] = digitsOnly.split(".");
  const decimal = decimalParts.join("").slice(0, 2);
  return decimalParts.length ? `${whole}.${decimal}` : whole;
};

const formatTransactionDate = (value = new Date()) =>
  new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

const getFullName = (source = {}) =>
  source.fullName ||
  source.name ||
  [source.firstName, source.lastName].filter(Boolean).join(" ").trim();

const getJoinedYear = (source = {}) => {
  const raw =
    source.joinedYear ||
    source.joined_year ||
    source.createdAt ||
    source.created_at ||
    "";
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return String(date.getFullYear());
};

const getBookingsCount = (provider = {}, account = {}) => {
  const raw =
    provider.bookingsCount ??
    provider.bookingCount ??
    provider.totalBookings ??
    provider.total_bookings ??
    provider.stats?.bookings ??
    account.bookingsCount ??
    account.bookingCount ??
    account.totalBookings ??
    account.total_bookings ??
    0;
  const count = Number(raw);
  return Number.isFinite(count) ? count : 0;
};

const resolveHostStatus = (bookingsCount) => {
  if (bookingsCount >= 10000) return "Superhost";
  if (bookingsCount >= 1000) return "Host";
  return "Sub-Host";
};

// A withdrawal starts life queued, not paid. Reporting it as "Successful" is
// how the receipt came to claim money had landed when nothing had been sent.
// Payouts are sent by hand today, so "awaiting payout" is the honest first
// state; it becomes "paid" only when the transfer has actually gone out.
const WITHDRAWAL_STATUS_LABELS = {
  pending: "Awaiting payout",
  processing: "Being processed",
  success: "Paid",
  failed: "Declined",
};

const formatWithdrawalStatus = (status) =>
  WITHDRAWAL_STATUS_LABELS[String(status || "").trim().toLowerCase()] || "Processing";

// Accounts saved through the bank form carry the server's own id; the fallback
// account derived from the saved payment profile has a synthetic one the API
// would reject, so it is left out and the server picks the default.
const isServerBankAccountId = (id) => /^[a-f\d]{24}$/i.test(String(id || ""));

const unwrapResponse = (response) => {
  if (!response) return {};
  return (
    response.data?.data?.profile ||
    response.data?.data?.user ||
    response.data?.data ||
    response.data?.profile ||
    response.data ||
    response.profile ||
    response
  );
};

const extractPaymentDetails = (payment = {}, provider = {}, account = {}) => {
  const providerPayment =
    payment ||
    provider.payment ||
    provider.pricingAndAccount ||
    provider.pricing ||
    provider.bankAccount ||
    provider.accountDetails ||
    provider.bank ||
    account.payment ||
    account.pricingAndAccount ||
    account.bankAccount ||
    account.accountDetails ||
    account.bank ||
    {};

  const bankName =
    providerPayment.bankName ||
    providerPayment.bank ||
    providerPayment.bank_name ||
    "";
  const bankCode = providerPayment.bankCode || providerPayment.bank_code || "";
  const accountName =
    providerPayment.accountName ||
    providerPayment.account_name ||
    "";
  const accountNumber =
    String(
      providerPayment.accountNumber ||
        providerPayment.account_number ||
        ""
    ).trim();

  if (!bankName || !accountNumber) {
    return [];
  }

  return [
    {
      id: `${bankCode || bankName}-${accountNumber}`,
      bankName,
      bankCode,
      accountName,
      accountNumber,
    },
  ];
};

const mapProviderToAssistant = (provider = {}, expertise = {}, account = {}, payment = {}) => {
  const providerUser = provider.user || {};
  const resolvedName =
    getFullName(provider) || getFullName(providerUser) || getFullName(account);
  const profilePicture =
    provider.profilePicture?.url ||
    (typeof provider.profilePicture === "string" ? provider.profilePicture : "");
  const highlights = normalizeImageList(
    provider.highlights ||
      provider.vehicle?.images ||
      provider.vehicle?.vehicleImages ||
      provider.vehicleImages ||
      provider.vehicle_images ||
      provider.profile?.vehicle?.images ||
      provider.profile?.vehicle?.vehicleImages
  );
  const services = normalizeAssistantServices(provider.services);
  const reviews = normalizeReviewList(provider.reviews);
  const reviewsCount =
    provider.reviewsCount ||
    provider.reviews_count ||
    provider.ratingCount ||
    provider.rating_count ||
    reviews.length ||
    0;
  const rating =
    provider.rating || provider.averageRating || provider.average_rating || defaultAssistant.rating;
  const derivedNotIncluded = deriveAssistantNotIncluded(services);
  const languages =
    (Array.isArray(expertise.languages) && expertise.languages.length
      ? expertise.languages
      : Array.isArray(provider.languages)
        ? provider.languages
        : []) || [];
  const specializations =
    (Array.isArray(expertise.specializations) && expertise.specializations.length
      ? expertise.specializations
      : Array.isArray(provider.specializations)
        ? provider.specializations
        : []) || [];
  const experienceYears =
    expertise.experienceYears != null ? expertise.experienceYears : provider.experienceYears;
  const currency = provider.wallet?.currency === "NGN" ? "₦" : provider.wallet?.currency || "₦";
  // Ledger = paid but still locked until trip confirmations; never the
  // available balance.
  const ledgerBalance =
    provider.wallet?.ledgerBalance ??
    provider.wallet?.ledger_balance ??
    provider.wallet?.lockedBalance ??
    provider.wallet?.locked_balance ??
    0;
  const joinedYear =
    getJoinedYear(provider) || getJoinedYear(providerUser) || getJoinedYear(account);
  const bookingsCount = getBookingsCount(provider, account);
  const hostStatus = resolveHostStatus(bookingsCount);
  const expectations = specializations.length
    ? `Specializations: ${specializations.join(", ")}.`
    : provider.expectations || defaultAssistant.expectations;

  return {
    ...defaultAssistant,
    id: provider.id || defaultAssistant.id,
    name: resolvedName || defaultAssistant.name,
    // No bio means no bio. Falling back to the sample profile's text put a
    // fabricated ten-year history — and a gender the product never asks for
    // ("He's known for...") — on the page of every assistant who had not
    // written one yet. The public profile has always fallen back to empty.
    about: provider.bio || provider.about || provider.description || "",
    servicesOffered: services.length ? services : defaultAssistant.servicesOffered,
    notIncluded: derivedNotIncluded,
    languages: languages.length ? languages : defaultAssistant.languages,
    image: profilePicture || defaultAssistant.image,
    highlights: highlights.length ? highlights : [],
    price: formatCurrency(provider.pricePerDay, currency),
    walletBalance: formatCurrency(provider.wallet?.balance, currency),
    walletLedgerBalance: formatCurrency(ledgerBalance, currency),
    currency,
    withdrawalAccounts: extractPaymentDetails(payment, provider, account),
    expectations,
    experienceYears,
    joinedYear,
    specializations,
    bookingsCount,
    hostStatus,
    reviews,
    reviewsCount,
    rating,
  };
};

const SelectBankField = ({
  label,
  value,
  onChange,
  options = [],
  placeholder,
  disabled = false,
}) => (
  <div className="space-y-2">
    <label className="text-[11px] font-medium text-[#344054]">{label}</label>
    <AppSelect
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      showCheckIcon={false}
      buttonClassName="rounded-md px-3 py-2 text-sm"
      optionsClassName="rounded-md"
      optionClassName="py-2 text-sm"
    />
  </div>
);

const ReceiptRow = ({ label, value, valueClassName = "" }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-[#667085]">{label}</span>
    <span className={`text-right font-medium text-[#344054] ${valueClassName}`}>
      {value || "—"}
    </span>
  </div>
);

function ProfileAssistant() {
  const { id } = useParams();
  const { state } = useLocation();
  const { profile, expertise } = useProfileStore();
  const { user } = useAuthStore();
  const [providerProfile, setProviderProfile] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const highlightsInputRef = useRef(null);
  const [isUploadingHighlights, setIsUploadingHighlights] = useState(false);
  const [highlightsError, setHighlightsError] = useState("");
  const [availableBanks, setAvailableBanks] = useState([]);
  const [bankNotice, setBankNotice] = useState("");
  const [isLoadingBanks, setIsLoadingBanks] = useState(false);
  const [linkedBanks, setLinkedBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState("");
  const [withdrawalStep, setWithdrawalStep] = useState(null);
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [withdrawalError, setWithdrawalError] = useState("");
  const [withdrawalSuccess, setWithdrawalSuccess] = useState("");
  const [isProcessingWithdrawal, setIsProcessingWithdrawal] = useState(false);
  const [withdrawalTransaction, setWithdrawalTransaction] = useState(null);
  const [bankForm, setBankForm] = useState({
    bank: "",
    accountNumber: "",
    accountName: "",
  });
  const [bankResolveError, setBankResolveError] = useState("");
  const [isResolvingBank, setIsResolvingBank] = useState(false);
  const [isSavingBank, setIsSavingBank] = useState(false);

  const fetchProviderData = async () => {
    const storedUser = user;

    const [profileResult, expertiseResult, accountResult, paymentResult] = await Promise.allSettled([
      providersService.getProfile(),
      providersService.getExpertise(),
      axiosInstance.get(ENDPOINTS.AUTH.ACCOUNT),
      providersService.getPayment(),
    ]);
    const profileResponse =
      profileResult.status === "fulfilled" ? profileResult.value : null;
    const expertiseResponse =
      expertiseResult.status === "fulfilled" ? expertiseResult.value : null;
    const accountResponse =
      accountResult.status === "fulfilled" ? accountResult.value : null;
    const paymentResponse =
      paymentResult.status === "fulfilled" ? paymentResult.value : null;
    const profile = unwrapResponse(profileResponse);
    const expertise = unwrapResponse(expertiseResponse);
    const account = unwrapResponse(accountResponse);
    const payment = paymentResponse && typeof paymentResponse === "object" ? paymentResponse : {};

    return mapProviderToAssistant(
      profile || {},
      expertise || {},
      account || storedUser || {},
      payment || {}
    );
  };

  const assistant = useMemo(() => {
    const incoming = state?.assistant || {};
    if (id) {
      return {
        ...defaultAssistant,
        ...incoming,
        id: id || incoming.id || defaultAssistant.id,
      };
    }
    return providerProfile
      ? { ...defaultAssistant, ...providerProfile }
      : {
          ...defaultAssistant,
          ...incoming,
          id: incoming.id || defaultAssistant.id,
        };
  }, [id, state, providerProfile]);

  const approvalSources = useMemo(
    () =>
      [
        profile,
        expertise,
        user,
      ].filter(Boolean),
    [profile, expertise, user]
  );
  const approvalState = useMemo(
    () => resolveApprovalState(...approvalSources),
    [approvalSources]
  );
  const canGoOnline = approvalState === "approved";
  const availableBalanceAmount = useMemo(
    () => parseCurrencyAmount(assistant.walletBalance),
    [assistant.walletBalance]
  );
  const availableBalanceLabel = assistant.walletBalance || "₦0.00";
  const ledgerBalanceLabel = assistant.walletLedgerBalance || "₦0.00";
  const selectedBank = linkedBanks.find((bank) => bank.id === selectedBankId) || null;

  useEffect(() => {
    if (id) {
      setIsLoadingProfile(false);
      return;
    }
    let active = true;

    const fetchProviderProfile = async () => {
      try {
        const mappedProfile = await fetchProviderData();
        if (!active) return;
        setProviderProfile(mappedProfile);
      } catch (error) {
        // Keep default profile data on failure.
      } finally {
        if (active) setIsLoadingProfile(false);
      }
    };

    fetchProviderProfile();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (!assistant.withdrawalAccounts?.length) return;
    setLinkedBanks((prev) => {
      const next = [...prev];
      assistant.withdrawalAccounts.forEach((account) => {
        if (!next.find((item) => item.id === account.id)) {
          next.push(account);
        }
      });
      return next;
    });
  }, [assistant.withdrawalAccounts]);

  useEffect(() => {
    if (!selectedBankId && linkedBanks.length) {
      setSelectedBankId(linkedBanks[0].id);
    }
    if (selectedBankId && !linkedBanks.find((bank) => bank.id === selectedBankId)) {
      setSelectedBankId(linkedBanks[0]?.id || "");
    }
  }, [linkedBanks, selectedBankId]);

  useEffect(() => {
    let active = true;

    const fetchBanks = async () => {
      setIsLoadingBanks(true);
      try {
        const response = await providersService.getBanks();
        const list = response?.banks || response?.data?.banks || [];
        if (!active) return;
        if (list.length) {
          setAvailableBanks(list);
          setBankNotice("");
        } else {
          setAvailableBanks(fallbackBanks);
          setBankNotice("Bank list is temporarily unavailable. Showing a fallback list.");
        }
      } catch {
        if (!active) return;
        setAvailableBanks(fallbackBanks);
        setBankNotice("Bank list is temporarily unavailable. Showing a fallback list.");
      } finally {
        if (active) setIsLoadingBanks(false);
      }
    };

    fetchBanks();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const selectedBankOption =
      availableBanks.find((item) => item.name === bankForm.bank) || {};
    const accountNumber = bankForm.accountNumber.trim();

    if (!selectedBankOption.code || accountNumber.length < 10) {
      setBankResolveError("");
      return;
    }

    const timer = setTimeout(async () => {
      setIsResolvingBank(true);
      setBankResolveError("");
      try {
        const response = await providersService.resolveBankAccount({
          accountNumber,
          bankCode: selectedBankOption.code,
        });
        const resolvedName = response?.accountName || response?.data?.accountName || "";
        if (active && resolvedName) {
          setBankForm((prev) => ({ ...prev, accountName: resolvedName }));
        }
      } catch (error) {
        if (!active) return;
        setBankResolveError(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Unable to resolve account name."
        );
      } finally {
        if (active) setIsResolvingBank(false);
      }
    }, 500);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [availableBanks, bankForm.accountNumber, bankForm.bank]);

  const handleHighlightsUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const validationError = files.map((file) => validateUploadFile(file)).find(Boolean);
    if (validationError) {
      setHighlightsError(validationError);
      event.target.value = "";
      return;
    }

    setHighlightsError("");
    setIsUploadingHighlights(true);

    try {
      // Append rather than replace: this screen adds to the existing set of
      // vehicle photos rather than swapping them out.
      await providersService.updateVehicle({
        vehicleImages: files,
        appendImages: true,
      });
      const mappedProfile = await fetchProviderData();
      setProviderProfile(mappedProfile);
    } catch (error) {
      setHighlightsError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to upload highlights. Please try again."
      );
    } finally {
      setIsUploadingHighlights(false);
      event.target.value = "";
    }
  };

  const openWithdrawModal = () => {
    setWithdrawalError("");
    setWithdrawalSuccess("");
    setWithdrawalStep("withdraw");
  };

  const closeWithdrawalFlow = () => {
    setWithdrawalStep(null);
    setWithdrawalAmount("");
    setWithdrawalError("");
    setWithdrawalSuccess("");
    setIsProcessingWithdrawal(false);
    setWithdrawalTransaction(null);
    setBankResolveError("");
    setIsSavingBank(false);
    setBankForm({
      bank: "",
      accountNumber: "",
      accountName: "",
    });
  };

  const openAddBankModal = () => {
    setWithdrawalError("");
    setBankResolveError("");
    setBankForm({
      bank: "",
      accountNumber: "",
      accountName: "",
    });
    setWithdrawalStep("add-bank");
  };

  const backToWithdrawModal = () => {
    setBankResolveError("");
    setWithdrawalStep("withdraw");
  };

  const handleAddBank = async () => {
    const selectedBankOption =
      availableBanks.find((item) => item.name === bankForm.bank) || {};

    if (!bankForm.bank) {
      setBankResolveError("Please select a bank.");
      return;
    }
    if (!bankForm.accountNumber.trim()) {
      setBankResolveError("Please enter your account number.");
      return;
    }
    if (!bankForm.accountName.trim()) {
      setBankResolveError("Please wait for the account name to resolve.");
      return;
    }

    setIsSavingBank(true);
    setBankResolveError("");

    try {
      await providersService.updatePayment({
        pricePerDay: parseCurrencyAmount(assistant.price),
        accountNumber: bankForm.accountNumber.trim(),
        accountName: bankForm.accountName.trim(),
        bankCode: selectedBankOption.code || "",
        bankName: bankForm.bank,
      });

      const nextBank = {
        id: `${selectedBankOption.code || bankForm.bank}-${bankForm.accountNumber.trim()}`,
        bankName: bankForm.bank,
        bankCode: selectedBankOption.code || "",
        accountName: bankForm.accountName.trim(),
        accountNumber: bankForm.accountNumber.trim(),
      };

      setLinkedBanks((prev) => {
        const filtered = prev.filter((item) => item.id !== nextBank.id);
        return [...filtered, nextBank];
      });
      setProviderProfile((prev) =>
        prev
          ? {
              ...prev,
              withdrawalAccounts: [
                ...(prev.withdrawalAccounts || []).filter((item) => item.id !== nextBank.id),
                nextBank,
              ],
            }
          : prev
      );
      setSelectedBankId(nextBank.id);
      setBankForm({
        bank: "",
        accountNumber: "",
        accountName: "",
      });
      setWithdrawalStep("withdraw");
    } catch (error) {
      setBankResolveError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to save this bank account."
      );
    } finally {
      setIsSavingBank(false);
    }
  };

  const handleWithdrawalSubmit = async () => {
    const amountValue = parseCurrencyAmount(withdrawalAmount);

    if (!amountValue || amountValue <= 0) {
      setWithdrawalError("Please enter a valid withdrawal amount.");
      return;
    }
    if (amountValue > availableBalanceAmount) {
      setWithdrawalError("Withdrawal amount cannot exceed your available balance.");
      return;
    }
    if (!selectedBank) {
      setWithdrawalError("Please select an account for this withdrawal.");
      return;
    }

    setWithdrawalError("");
    setIsProcessingWithdrawal(true);

    try {
      const response = await providersService.requestWithdrawal({
        amount: amountValue,
        ...(isServerBankAccountId(selectedBank.id)
          ? { bankAccountId: selectedBank.id }
          : {}),
      });

      const withdrawal = unwrapResponse(response)?.withdrawal || {};
      const currency = assistant.currency || "₦";

      setWithdrawalTransaction({
        id: withdrawal.id ? `#${withdrawal.id}` : "—",
        amount: withdrawal.amount ?? amountValue,
        amountLabel: formatCurrency(withdrawal.amount ?? amountValue, currency),
        bankName: withdrawal.bank?.bankName || selectedBank.bankName,
        accountName: withdrawal.bank?.accountName || selectedBank.accountName,
        accountNumber: withdrawal.bank?.accountNumber || selectedBank.accountNumber,
        description: "Withdraw To Bank",
        dateLabel: formatTransactionDate(withdrawal.timestamp),
        status: formatWithdrawalStatus(withdrawal.status),
        rawStatus: String(withdrawal.status || "pending").toLowerCase(),
      });

      // The balance belongs to the server. Re-reading it is what makes the card
      // agree with the wallet after a withdrawal — subtracting locally only
      // looked right until the next refresh, which is how a withdrawn balance
      // appeared to come back.
      const mappedProfile = await fetchProviderData();
      setProviderProfile(mappedProfile);

      setWithdrawalSuccess(
        "Your withdrawal request has been received. The money will be sent to your account shortly."
      );
      setWithdrawalStep("success");
    } catch (error) {
      setWithdrawalError(
        getApiErrorMessage(error, "Unable to process this withdrawal. Please try again.")
      );
    } finally {
      setIsProcessingWithdrawal(false);
    }
  };

  const openReceiptModal = () => {
    setWithdrawalStep("receipt");
  };

  const languageList = useMemo(() => {
    const normalized = normalizeLanguages(assistant.languages);
    if (normalized.length > 0) return normalized;
    return normalizeLanguages(defaultAssistant.languages);
  }, [assistant.languages]);
  const joinedMeta = useMemo(() => {
    const items = [];
    if (assistant.joinedYear) items.push(`Joined in ${assistant.joinedYear}`);
    if (assistant.experienceYears) items.push(`${assistant.experienceYears} years experience`);
    return items.length ? items.join(" • ") : "Joined in 2018";
  }, [assistant.joinedYear, assistant.experienceYears]);
  const reviewList = useMemo(
    () => normalizeReviewList(assistant.reviews),
    [assistant.reviews]
  );
  const { data: availabilityData, isLoading: isAvailabilityLoading } = useAvailabilityQuery();
  const updateAvailability = useUpdateAvailabilityMutation();
  const isOnline = availabilityData?.isOnline || availabilityData?.data?.isOnline || false;
  const isTogglingOnline = updateAvailability.isPending;

  const handleToggleOnline = async () => {
    if (!canGoOnline || isTogglingOnline) return;
    await updateAvailability.mutateAsync(!isOnline);
  };

  const hasReviews = reviewList.length > 0;
  const displayReviewsCount = useMemo(() => {
    const count = Number(assistant.reviewsCount || 0);
    return Number.isFinite(count) && count > 0 ? count : reviewList.length;
  }, [assistant.reviewsCount, reviewList.length]);
  const profileMeta = useMemo(() => {
    if (displayReviewsCount > 0) {
      return `${displayReviewsCount} reviews • ${assistant.hostStatus}`;
    }
    return assistant.hostStatus;
  }, [assistant.hostStatus, displayReviewsCount]);
  const highlightOwnerName = (assistant.name || "Assistant").split(" ")[0];

  // const assistantsNearby = Array.from({ length: 4 }, (_, index) => ({
  //   id: `nearby-${index}`,
  //   name: assistant.name,
  //   rating: assistant.rating,
  //   location: assistant.location,
  //   languages: languageList.join(", "),
  //   image: "/images/travel-img.png",
  // }));

  const [openFaq, setOpenFaq] = useState(0);

  const Shimmer = ({ className = '' }) => (
    <div className={`animate-pulse rounded-xl bg-gray-200 ${className}`} />
  );

  if (isLoadingProfile) {
    return (
      <div className="min-h-screen">
        <main className="w-full mx-auto lg:max-w-[98%]">
          <div className="grid grid-cols-1 gap-20 lg:grid-cols-[1.1fr_1fr] lg:items-start">
            {/* ── Left column: avatar + name + action button + about ── */}
            <div className="flex flex-col items-center gap-6 text-center">
              {/* Avatar + name block */}
              <div className="flex flex-col items-center gap-4">
                <Shimmer className="h-32 w-32 rounded-full" />
                <div className="flex flex-col items-center gap-2">
                  <Shimmer className="h-8 w-48 rounded-lg" />
                  <Shimmer className="h-4 w-56 rounded-lg" />
                  <Shimmer className="h-4 w-32 rounded-lg" />
                </div>
              </div>
              {/* Go Online button */}
              <Shimmer className="h-12 w-full max-w-3xl rounded-lg" />
              {/* About section */}
              <div className="w-full max-w-3xl space-y-3 text-left">
                <Shimmer className="h-6 w-20 rounded" />
                <Shimmer className="h-4 w-full rounded" />
                <Shimmer className="h-4 w-5/6 rounded" />
                <Shimmer className="h-4 w-4/6 rounded" />
              </div>
              {/* Languages */}
              <div className="w-full max-w-3xl space-y-3 text-left">
                <Shimmer className="h-6 w-28 rounded" />
                {[1, 2, 3].map((i) => <Shimmer key={i} className="h-4 w-24 rounded" />)}
              </div>
            </div>

            {/* ── Right column: wallet + services ── */}
            <div className="flex flex-col gap-8">
              {/* Wallet card */}
              <Shimmer className="h-48 w-full rounded-2xl" />
              {/* Expectations */}
              <div className="space-y-3">
                <Shimmer className="h-6 w-36 rounded" />
                {[1, 2, 3].map((i) => <Shimmer key={i} className="h-4 w-full rounded" />)}
              </div>
              {/* Services offered */}
              <div className="space-y-3">
                <Shimmer className="h-6 w-40 rounded" />
                {[1, 2, 3, 4].map((i) => <Shimmer key={i} className="h-4 w-36 rounded" />)}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className=" min-h-screen">
      <main className=" w-full  mx-auto  lg:max-w-[98%] md:space-y-16">
        <div className="grid grid-cols-1 gap-20 lg:grid-cols-[1.1fr_1fr] lg:items-start">
          <div className="flex flex-col items-center gap-6 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-[#570033]/20 shadow-sm">
                <img
                  src={assistant.image}
                  alt={assistant.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="space-y-1">
                <h1 className="text-3xl font-bold text-[#570033] leading-tight">
                  {assistant.name}
                </h1>
                <p className="text-sm text-slate-600">{joinedMeta}</p>
                <p className="text-sm text-slate-600">{profileMeta}</p>
              </div>
            </div>

            <div className="flex w-full flex-col gap-3 max-w-3xl">
              <button
                type="button"
                disabled={!canGoOnline || isTogglingOnline || isAvailabilityLoading}
                aria-disabled={!canGoOnline || isTogglingOnline || isAvailabilityLoading}
                onClick={handleToggleOnline}
                className={`w-full rounded-lg px-4 py-3 text-center text-base font-semibold text-white transition ${
                  canGoOnline && !isTogglingOnline
                    ? "bg-[#570033] hover:bg-[#450029]"
                    : "cursor-not-allowed bg-[#E6C8D9] text-white"
                }`}
              >
                <span className="flex items-center justify-center gap-3">
                  {isTogglingOnline ? (
                    <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : isOnline ? (
                    <ChevronsLeft className="h-7 w-7" strokeWidth={2.2} />
                  ) : null}
                  <span>
                    {isTogglingOnline
                      ? isOnline
                        ? "Going Offline..."
                        : "Going Online..."
                      : isOnline
                        ? "Go Offline"
                        : "Go Online"}
                  </span>
                  {!isTogglingOnline && !isOnline ? (
                    <ChevronsRight className="h-7 w-7" strokeWidth={2.2} />
                  ) : null}
                </span>
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-[#570033]">Wallet</h2>
            <div className="rounded-2xl bg-[#570033] text-white p-6 shadow-md">
              <div className="flex items-center justify-between text-sm font-semibold">
                <div className="flex items-center gap-2">
                  {/* <span className="inline-flex h-5 w-5 items-center justify-center rounded border border-white/70 text-xs">
                    ☐ ☐☐ 
                  </span>*/}
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded border border-white/70">
                    <Wallet className="h-4 w-4 text-white" strokeWidth={1.8} />
                  </span>

                  Available Balance
                </div>
                <button
                  type="button"
                  onClick={openWithdrawModal}
                  className="underline"
                >
                  Withdraw
                </button>
              </div>
              <p className="mt-4 text-3xl font-bold">
                {availableBalanceLabel}
              </p>
              <div className="mt-6 flex items-center justify-between text-sm font-medium">
                <p className="text-lg">Ledger Balance {ledgerBalanceLabel}</p>
                <div className="flex gap-1  ">
                  <img src="/icons/small-vector.svg" alt="" />
                  <span className="text-lg ">Pulo wallet</span>
                  </div>
                
              </div>
            </div>
          </div>
        </div>

        <section className="max-md:mt-10 grid grid-cols-1 lg:grid-cols-2 gap-x-36">
          <div className="space-y-8">
            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-[#570033]">About</h3>
              <p className="text-base text-slate-700 font-medium leading-relaxed">
                {assistant.about || (
                  <span className="text-slate-400">
                    You haven't added a bio yet. Travellers see this first —
                    add one from Settings.
                  </span>
                )}
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-[#570033]">Languages</h3>
              <ul className="space-y-2 text-base text-slate-700 list-disc list-inside">
                {languageList.map((lang) => (
                  <li key={lang}>{lang}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="max-lg:mt-6 space-y-8">
            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-[#570033]">Expectations</h3>
              {assistant.specializations?.length ? (
                <ul className="space-y-2 text-base text-slate-800">
                  {assistant.specializations.map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-[#570033]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-base text-slate-700 font-medium leading-relaxed">
                  {assistant.expectations}
                </p>
              )}
            </div>

            <div className="flex justify-between">
              <div className="space-y-3">
                <h3 className="text-2xl font-bold text-[#570033]">Services Offered</h3>
                <ul className="space-y-2 text-base text-slate-800">
                  {assistant.servicesOffered.map((service) => (
                    <li key={service} className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-[#570033]" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-3">
                <h3 className="text-2xl font-bold text-[#570033]">Not Included</h3>
                <ul className="space-y-2 text-base text-slate-800">
                  {assistant.notIncluded.map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <CloseIcon className="w-5 h-5 text-[#b42318]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="max-md:my-10 grid gap-y-10 lg:grid-cols-2 sm:gap-x-32">
          <div className="space-y-10">
            {hasReviews ? (
              <div className="space-y-6">
                <div className="flex items-center justify-between gap-1">
                  <h3 className="text-2xl font-bold text-[#570033]">Reviews</h3>
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <span>{assistant.rating}</span>
                    <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
                    <span className="text-slate-500">({displayReviewsCount} Reviews)</span>
                  </div>
                </div>
                <hr />
                <div>
                  {reviewList.map((review) => (
                    <div key={review.id} className="flex items-center gap-3 p-4">
                      <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-slate-100">
                        <img
                          src={review.avatar || "/images/testimonial-passport.jpg"}
                          alt={review.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
                          {review.name}
                        </div>
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                          {review.date ? <span>{review.date}</span> : null}
                          {review.date && review.rating != null ? (
                            <span className="h-3 w-px bg-slate-700"></span>
                          ) : null}
                          {review.rating != null ? (
                            <span className="flex items-start gap-1 font-medium text-slate-600">
                              {review.rating}
                              <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
                            </span>
                          ) : null}
                        </div>
                        <p className="text-sm font-medium leading-relaxed text-slate-700">
                          {review.comment}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="max-md:mt-10 space-y-8 sm:w-[92%]">
              <h3 className="text-2xl font-bold text-[#570033]">FAQ</h3>
              <div className="divide-y-[.2px] divide-[#61013B]">
                <hr />
                {assistant.faqs.map((faq, idx) => {
                  const isOpen = openFaq === idx;
                  return (
                    <div key={faq.question} className="py-3">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between py-2 text-left text-base font-semibold text-slate-900"
                        onClick={() => setOpenFaq(isOpen ? null : idx)}
                      >
                        {faq.question}
                        <ChevronDown
                          className={`h-5 w-5 text-slate-600 transition-transform ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {isOpen && (
                        <p className="pb-2 text-sm leading-relaxed text-slate-700">
                          {faq.answer}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-[#570033]">
              {highlightOwnerName}&apos;s Highlights
            </h3>
            {assistant.highlights.length ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {assistant.highlights.map((src, idx) => (
                  <div key={idx} className="h-36 rounded-xl overflow-hidden">
                    <img
                      src={src}
                      alt={`Highlight ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-[28px] border border-[#D0D5DD] bg-white p-8 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-[#D0D5DD] bg-white text-[#570033]">
                  <Plus className="h-5 w-5" />
                </div>
                <p className="mt-4 text-base font-semibold text-[#570033]">
                  {isUploadingHighlights ? "Uploading..." : "Add Highlights"}
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  Upload photos from your device to showcase your work.
                </p>
                <button
                  type="button"
                  onClick={() => highlightsInputRef.current?.click()}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg border border-[#570033] px-4 py-2 text-sm font-semibold text-[#570033] transition hover:bg-[#f7eef4]"
                  disabled={isUploadingHighlights}
                >
                  {isUploadingHighlights ? "Uploading..." : "Add Highlights"}
                </button>
                {highlightsError ? (
                  <p className="mt-3 text-sm font-semibold text-red-600">{highlightsError}</p>
                ) : null}
              </div>
            )}
            <input
              ref={highlightsInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleHighlightsUpload}
            />
          </div>
        </section>

        {/* <section className="space-y-3">
          <div className="flex items-center justify-between">
            <SectionTitle className="text-sm">Pulo Assistants within Abuja</SectionTitle>
            <NavLink
              to="/travel-assistant"
              className="text-xs sm:text-sm font-semibold text-[#570033]"
            >
              See more
            </NavLink>
          </div>
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 sm:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {assistantsNearby.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm p-[6px] flex-shrink-0 w-[85%] sm:w-auto sm:flex-shrink snap-center"
              >
                <div className="relative">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-60 object-cover object-top rounded-xl"
                  />
                </div>

                <div className="pt-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <p className="text-base font-semibold text-black">
                        {item.name}
                      </p>
                      <div className="flex items-center gap-1 text-sm font-medium text-black">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        <span>{item.rating}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="p-1 rounded-full border border-gray-200 hover:border-[#570033] transition-colors"
                    >
                      <Heart className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-2 text-sm font-medium text-black">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-black" />
                      <span>{item.location}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-1">
                        <Globe2 className="w-4 h-4 text-black" />
                        <span>{item.languages}</span>
                      </div>
                      <NavLink
                        to="/auth/login"
                        className="text-sm font-semibold text-[#570033] underline underline-offset-2"
                      >
                        View
                      </NavLink>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section> */}

      </main>

      {withdrawalStep ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={closeWithdrawalFlow}
        >
          {withdrawalStep === "withdraw" ? (
            <div
              className="w-full max-w-md rounded-[20px] bg-white p-5 shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="space-y-4">
                <h3 className="text-center text-base font-semibold text-[#101828]">
                  Withdraw
                </h3>

                <div className="space-y-2">
                  <label className="text-[11px] font-medium text-[#344054]">
                    Withdraw Amount
                  </label>
                  <input
                    type="text"
                    value={withdrawalAmount}
                    onChange={(event) => {
                      setWithdrawalError("");
                      setWithdrawalAmount(sanitizeCurrencyInput(event.target.value));
                    }}
                    placeholder="Amount"
                    inputMode="decimal"
                    autoComplete="off"
                    className="w-full rounded-md border border-[#D0D5DD] px-3 py-2 text-sm font-medium text-[#344054] placeholder:text-[#98A2B3] focus:border-[#61013B] focus:outline-none focus:ring-2 focus:ring-[#61013B]/10"
                  />
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="inline-flex items-center gap-1 font-medium text-[#16A34A]">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Available Balance: {availableBalanceLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => setWithdrawalAmount(String(availableBalanceAmount))}
                      className="font-medium text-[#7A054A]"
                    >
                      Max
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-medium text-[#344054]">
                      Select Account
                    </label>
                    <button
                      type="button"
                      onClick={openAddBankModal}
                      className="text-[11px] font-semibold text-[#7A054A] underline"
                    >
                      Add New
                    </button>
                  </div>

                  {linkedBanks.length ? (
                    <div className="space-y-2">
                      {linkedBanks.map((bank) => {
                        const isActive = bank.id === selectedBankId;
                        return (
                          <button
                            key={bank.id}
                            type="button"
                            onClick={() => setSelectedBankId(bank.id)}
                            className={`flex w-full items-center justify-between rounded-xl border px-3 py-3 text-left transition ${
                              isActive
                                ? "border-[#D9ABC4] bg-[#FFF9FC]"
                                : "border-[#EAECF0] bg-white"
                            }`}
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 text-sm font-semibold text-[#344054]">
                                <Building2 className="h-4 w-4 text-[#667085]" />
                                <span>{bank.bankName}</span>
                              </div>
                              <p className="mt-1 truncate text-[11px] font-medium text-[#667085]">
                                {bank.accountNumber}, {bank.accountName}
                              </p>
                            </div>
                            <span
                              className={`h-4 w-4 rounded-full border ${
                                isActive
                                  ? "border-[#C97AAA] bg-[#C97AAA] ring-2 ring-[#F7E7F0]"
                                  : "border-[#D0D5DD] bg-white"
                              }`}
                            />
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="rounded-md border border-dashed border-[#D0D5DD] px-3 py-3 text-sm text-[#667085]">
                      No withdrawal account added yet.
                    </p>
                  )}
                </div>

                {withdrawalError ? (
                  <div className="flex items-center gap-2 rounded-md bg-[#FDECEC] px-3 py-2 text-sm font-medium text-[#B42318]">
                    <AlertCircle className="h-4 w-4" />
                    <span>{withdrawalError}</span>
                  </div>
                ) : null}

                {bankNotice ? (
                  <p className="text-xs font-medium text-[#667085]">{bankNotice}</p>
                ) : null}

                <button
                  type="button"
                  onClick={handleWithdrawalSubmit}
                  disabled={isProcessingWithdrawal}
                  className="w-full rounded-md bg-[#61013B] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#450029] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isProcessingWithdrawal ? "Processing..." : "Withdraw"}
                </button>
              </div>
            </div>
          ) : null}

          {withdrawalStep === "add-bank" ? (
            <div
              className="w-full max-w-md rounded-[20px] bg-white p-5 shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="space-y-4">
                <h3 className="text-center text-base font-semibold text-[#101828]">
                  Add New Bank
                </h3>

                <SelectBankField
                  label="Bank"
                  value={bankForm.bank}
                  onChange={(value) => {
                    setBankResolveError("");
                    setBankForm((prev) => ({ ...prev, bank: value, accountName: "" }));
                  }}
                  options={availableBanks.map((bank) => bank.name)}
                  placeholder={isLoadingBanks ? "Loading banks..." : "Select Bank"}
                  disabled={isLoadingBanks}
                />

                {bankNotice ? (
                  <p className="text-xs font-medium text-[#667085]">{bankNotice}</p>
                ) : null}

                <div className="space-y-2">
                  <label className="text-[11px] font-medium text-[#344054]">
                    Account Number
                  </label>
                  <input
                    type="text"
                    value={bankForm.accountNumber}
                    onChange={(event) => {
                      setBankResolveError("");
                      setBankForm((prev) => ({
                        ...prev,
                        accountNumber: event.target.value,
                        accountName: "",
                      }));
                    }}
                    placeholder="1234567890"
                    inputMode="numeric"
                    maxLength={10}
                    className="w-full rounded-md border border-[#D0D5DD] px-3 py-2 text-sm font-medium text-[#344054] placeholder:text-[#98A2B3] focus:border-[#61013B] focus:outline-none focus:ring-2 focus:ring-[#61013B]/10"
                  />
                  <p className="min-h-[1.25rem] text-right text-[11px] font-medium text-[#667085]">
                    {isResolvingBank
                      ? "Resolving account name..."
                      : bankForm.accountName || ""}
                  </p>
                </div>

                {bankResolveError ? (
                  <div className="flex items-center gap-2 rounded-md bg-[#FDECEC] px-3 py-2 text-sm font-medium text-[#B42318]">
                    <AlertCircle className="h-4 w-4" />
                    <span>{bankResolveError}</span>
                  </div>
                ) : null}

                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={handleAddBank}
                    disabled={isSavingBank}
                    className="w-full rounded-md bg-[#61013B] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#450029] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSavingBank ? "Saving..." : "Set"}
                  </button>
                  <button
                    type="button"
                    onClick={backToWithdrawModal}
                    disabled={isSavingBank}
                    className="w-full rounded-md border border-[#C97AAA] px-4 py-3 text-sm font-semibold text-[#7A054A] transition hover:bg-[#F8EEF4] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {withdrawalStep === "success" ? (
            <div
              className="w-full max-w-sm rounded-[20px] bg-white px-6 py-8 text-center shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#E7F6EA]">
                <CheckCircle2 className="h-10 w-10 text-[#19913A]" />
              </div>
              <h3 className="mt-6 text-2xl font-semibold text-[#7A054A]">
                Withdrawal Successful
              </h3>
              <p className="mt-3 text-sm leading-6 text-[#667085]">
                {withdrawalSuccess || "Your withdrawal request is being processed to your account."}
              </p>

              <div className="mt-6 space-y-3">
                <button
                  type="button"
                  onClick={closeWithdrawalFlow}
                  className="w-full rounded-md bg-[#61013B] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#450029]"
                >
                  Back to Home
                </button>
                <button
                  type="button"
                  onClick={openReceiptModal}
                  className="w-full text-sm font-semibold text-[#7A054A] underline"
                >
                  View Receipt
                </button>
              </div>
            </div>
          ) : null}

          {withdrawalStep === "receipt" ? (
            <div
              className="w-full max-w-md rounded-[20px] bg-white p-5 shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="rounded-xl border border-[#EAECF0] p-4">
                <h3 className="text-center text-base font-semibold text-[#344054]">
                  Transaction Details
                </h3>
                <div className="mt-4 rounded-lg border border-[#EAECF0] px-4 py-4 text-center">
                  <p className="text-[11px] font-medium text-[#667085]">
                    Amount Withdrawn:
                  </p>
                  <p className="mt-2 text-2xl font-bold text-[#7A054A]">
                    {withdrawalTransaction?.amountLabel || "₦0.00"}
                  </p>
                </div>

                <div className="mt-5 space-y-3 text-[11px]">
                  <ReceiptRow label="Transaction ID" value={withdrawalTransaction?.id} />
                  <ReceiptRow label="Description" value={withdrawalTransaction?.description} />
                  <ReceiptRow label="Destination Bank" value={withdrawalTransaction?.bankName} />
                  <ReceiptRow label="Account Name" value={withdrawalTransaction?.accountName} />
                  <ReceiptRow label="Account Number" value={withdrawalTransaction?.accountNumber} />
                  <ReceiptRow label="Transaction Date" value={withdrawalTransaction?.dateLabel} />
                  <ReceiptRow
                    label="Status"
                    value={withdrawalTransaction?.status}
                    valueClassName={
                      withdrawalTransaction?.rawStatus === "success"
                        ? "text-[#16A34A]"
                        : withdrawalTransaction?.rawStatus === "failed"
                        ? "text-red-600"
                        : "text-[#B26700]"
                    }
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={closeWithdrawalFlow}
                className="mt-5 w-full rounded-md bg-[#61013B] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#450029]"
              >
                Report Issues
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

    </div>
  );
}

export default ProfileAssistant;
